import React, { useState } from 'react';
import styled from 'styled-components';
import { StyledAvatar } from './StyledAvatar';
import { getProfileCredentials } from 'src/utils/auth';
import { useAuth } from 'src/state/auth/auth';
import { UserModal } from 'src/components/modals/UserModal';
import { useCache } from 'src/graphql/useCache';
import { mpEvent, MPEvents, Tags } from 'src/utils/mixpanel';

const AvatarWrapper = styled.div<{ $cursorPointer?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  grid-gap: 10px;
  max-width: 100%;
  &:hover {
    cursor: ${({ $cursorPointer }) => ($cursorPointer ? 'pointer' : 'default')};
    .table-link {
      color: ${({ theme }) => theme.colors.gp1};
    }
  }
`;

interface TableUserItemProps {
  profilePhotoPath?: string | null;
  name?: string | null;
  interactive?: {
    onClick?: () => void;
    email?: string | null;
    scope: 'workspace' | 'organisation';
    workspaceId?: string;
    orgId?: string;
    providedUserId?: string;
  };
}

export const TableUserItem: React.FC<TableUserItemProps> = ({ profilePhotoPath, name, interactive }) => {
  const { getRealUserId, workspaceId, orgId } = useAuth();
  const [profileModalId, setProfileModalId] = useState<string | null>(null);
  const userId = interactive?.providedUserId ? interactive.providedUserId : getRealUserId(interactive?.email);
  const { refetchQuery } = useCache();

  return (
    <>
      {profileModalId && interactive && (
        <UserModal
          onClose={() => setProfileModalId(null)}
          externalUserId={+profileModalId}
          externalUserEmail={interactive.email || undefined}
          onUserUpdated={() =>
            refetchQuery({
              include:
                interactive.scope === 'workspace'
                  ? ['usersEmails', 'workspace', 'workspaceMembers']
                  : ['usersEmails', 'organisation'],
            })
          }
          workspaceId={interactive?.scope === 'workspace' ? interactive.workspaceId || workspaceId : undefined}
          orgId={interactive?.scope === 'organisation' ? interactive.orgId || orgId : undefined}
        />
      )}
      <AvatarWrapper
        onClick={() => {
          interactive?.onClick?.();
          if (userId) {
            setProfileModalId(userId);
            mpEvent(MPEvents.ButtonClick, {
              button: '[user click]',
              value: userId,
              tags: [interactive?.scope?.toUpperCase(), 'USER'].filter(Boolean) as Tags[],
            });
          }
        }}
        $cursorPointer={!!userId}
      >
        <StyledAvatar src={profilePhotoPath || ''}>{getProfileCredentials(name || '')}</StyledAvatar>
        <span className={`elipsis ${userId ? 'table-link' : ''}`}>{name}</span>
      </AvatarWrapper>
    </>
  );
};

interface PlainUserLinkProps {
  email: string;
  name?: string | null | number;
  userId?: string | null;
}
export const PlainUserLink = ({ email, name, userId }: PlainUserLinkProps) => {
  const { getRealUserId, workspaceId } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const id = userId ? userId : getRealUserId(email);
  const { refetchQuery } = useCache();

  return (
    <span onClick={(e) => e.stopPropagation()}>
      {isOpen && (
        <UserModal
          onClose={() => setIsOpen(false)}
          // @ts-ignore
          externalUserId={String(id)}
          externalUserEmail={email}
          onUserUpdated={() => refetchQuery({ include: ['usersEmails', 'workspace', 'workspaceMembers'] })}
          workspaceId={workspaceId}
        />
      )}
      {id ? (
        <span
          className="table-link"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(true);
          }}
        >
          {name}
        </span>
      ) : (
        <span onClick={(e) => e.stopPropagation()}>{name}</span>
      )}
    </span>
  );
};
